<template>
  <slot v-if="!loading && recentActivities.length" name="header"></slot>

  <RecentActivitySkeleton
    v-if="loading"
    :compact="viewportFit"
    v-bind="$attrs"
  />

  <!--
    LEGACY path (flag OFF): retain the original horizontal-scroll strip
    (overflow-x-auto hide-scrollbar) unchanged.
  -->
  <ul
    v-else-if="!viewportFit"
    v-bind="$attrs"
    class="flex flex-row gap-2 sm:gap-3 justify-between overflow-x-auto hide-scrollbar"
  >
    <li v-for="activity in recentActivities" :key="activity.date">
      <UserAvatarAndName
        :user-fragment="activity.user as UserAvatarAndNameType"
        :has-paid-membership="
          activity.user_is_superfan ??
          activity.user_has_paid_membership ??
          false
        "
        :badge-label="activity.points ?? 0"
        :badge-style="activity.has_multiplier_applied ? 'orange' : 'gold'"
        aspect="square"
      >
        <p class="text-xs text-text truncate w-full font-sans">
          {{ activity.location_label ?? activity.short_title }}
        </p>

        <p class="text-xs text-white/60">
          {{ activity.formattedDate }}
        </p>
      </UserAvatarAndName>
    </li>
  </ul>

  <!--
    NEW layout path (flag ON, viewportFit=true): non-scrolling flex row that
    clips at the container boundary. The ref `listEl` is measured by
    useElementSize so we know exactly how many fixed-width items fit. Items
    beyond visibleCount are sliced away client-side (the server-windowed
    limit:12 query is unchanged). The container naturally narrows when chat
    is open because it lives inside the page-column — no special chat-aware
    logic is required.
  -->
  <ul
    v-else
    ref="listEl"
    v-bind="$attrs"
    class="flex flex-row gap-9 overflow-hidden flex-nowrap"
  >
    <li
      v-for="activity in visibleActivities"
      :key="activity.date"
      class="shrink-0"
    >
      <UserAvatarAndName
        :user-fragment="activity.user as UserAvatarAndNameType"
        :has-paid-membership="
          activity.user_is_superfan ??
          activity.user_has_paid_membership ??
          false
        "
        :badge-label="activity.points ?? 0"
        badge-style="gold"
        aspect="square"
        compact
        light-surface
        :hide-name="activity.has_multiplier_applied"
      >
        <!--
          Matches the Figma "Meanwhile…" activity item. The
          first caption line is EITHER the username (rendered by
          UserAvatarAndName, dark `text-text`) OR — for an action that earned
          the membership point multiplier — the red "Max" badge that REPLACES
          the name (so `hide-name` suppresses the username above). Then the
          2-line `text-[13px]` "Earn points for {action}" caption, then the
          relative time.
        -->
        <span
          v-if="activity.has_multiplier_applied"
          class="inline-flex items-center gap-1 rounded-md bg-fill-brand px-1 py-0.5 text-sm font-semibold leading-5 text-text-on-brand"
          data-testid="activity-max-badge"
        >
          {{ $t("channel.recentActivity.maxMultiplier") }}
          <Icon icon="mdi:lightning-bolt" class="size-3.5" aria-hidden="true" />
        </span>

        <p
          v-if="activityActionLabel(activity)"
          class="line-clamp-2 w-full text-[13px] font-semibold leading-5 text-text"
        >
          {{
            $t("channel.recentActivity.earnPointsFor", {
              action: activityActionLabel(activity),
            })
          }}
        </p>

        <p class="text-xs font-medium leading-5 text-text-tertiary">
          {{ activity.formattedDate }}
        </p>
      </UserAvatarAndName>
    </li>
  </ul>
</template>

<script setup lang="ts">
import { graphql as gql } from "@/generated";
import { useChannelStore } from "@/stores/channel.store";
import { computed, ref, watch } from "vue";
import { Icon } from "@iconify/vue";
import UserAvatarAndName, {
  type UserAvatarAndName as UserAvatarAndNameType,
} from "./user-avatar-and-name.vue";
import { useUserStore } from "@/stores/user.store";
import { useQuery } from "@urql/vue";
import { useLoyaltyNotifications } from "../../features/channel/services/use-loyalty-notifications";
import { useTranslateObjectSimple } from "@/stores/translation.store";
import RecentActivitySkeleton from "@/components/ui/skeleton/recent-activity-skeleton.vue";
import { useElementSize } from "@vueuse/core";

// ---- Props ---------------------------------------------------------------

interface Props {
  /**
   * When true (new layout, flag ON): renders a non-scrolling flex row that
   * clips at the container width, slicing to only the items that fit.
   * When false/unset (legacy, flag OFF): renders the original
   * overflow-x-auto horizontal-scroll strip unchanged.
   */
  viewportFit?: boolean;
  /**
   * When true the row is hosted inside the shared unified hero panel —
   * channel.vue draws the panel chrome and the "MEANWHILE…" heading, so the
   * row contributes only its items. Declared as a
   * prop (rather than letting it fall through to `$attrs`) so the boolean is
   * typed and never leaks onto the rendered `<ul>` as a DOM attribute.
   */
  bare?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  viewportFit: false,
  bare: false,
});

// ---- Measurement (new layout only) ---------------------------------------

/**
 * Ref bound to the <ul> element in the viewport-fit path.
 * useElementSize returns { width: 0, height: 0 } when the target is null
 * (SSR / before mount), so the slice defaults to 0 and expands once the
 * DOM is ready — no explicit onMounted guard is needed.
 */
const listEl = ref<HTMLElement | null>(null);
const { width: containerWidth } = useElementSize(listEl);

/**
 * Distinguishes "not yet measured" (useElementSize reports width 0 before the
 * ResizeObserver fires on first paint) from "measured and zero items fit". Until
 * the container has a real width we must NOT fall back to the full list (that
 * causes a flash of every item before the slice kicks in), so this gate lets the
 * viewport-fit path render nothing until the first measurement lands.
 */
const isMeasured = computed(() => containerWidth.value > 0);

/**
 * Fixed width per activity item (avatar + label column) in pixels. Matches the
 * `compact` UserAvatarAndName column in the Figma master (135px: a 60px avatar
 * centered in a 135px column so the 32px coin/flag badges have room to overlap).
 * Adjust if the design token changes.
 */
const ITEM_WIDTH_PX = 135;

/**
 * Gap between items in pixels, matching the `gap-9` (36px) Tailwind class on the
 * viewport-fit `<ul>` — the Figma master spaces the 135px columns on a 171px
 * pitch (171 − 135 = 36).
 */
const ITEM_GAP_PX = 36;

const visibleCount = computed(() => {
  if (!props.viewportFit || containerWidth.value === 0) return 0;
  // floor( (containerWidth + gap) / (itemWidth + gap) )
  // Adding one gap to the numerator accounts for the missing trailing gap.
  const count = Math.floor(
    (containerWidth.value + ITEM_GAP_PX) / (ITEM_WIDTH_PX + ITEM_GAP_PX),
  );
  if (import.meta.env.DEV) {
    console.debug(
      `[ChannelRecentActivityRow] containerWidth=${containerWidth.value} → visibleCount=${count}`,
    );
  }
  return Math.max(0, count);
});

// ---- Loyalty notification regen ------------------------------------------

const { notifications } = useLoyaltyNotifications();

/**
 * We use this to regenerate the recent-activity-row when the user
 * performs an action that generates a loyalty notification.
 */
const lastNotificationId = computed(
  () => notifications[notifications.length - 1]?.id ?? null,
);

// ---- GraphQL query -------------------------------------------------------

const ChannelRecentActivityDocument = gql(/* GraphQL */ `
  query ChannelRecentActivity(
    $channel_id: String!
    $limit: Float!
    $community_id: String
  ) {
    recentUserChannelActivities(
      channel_id: $channel_id
      limit: $limit
      community_id: $community_id
    ) {
      date
      name
      short_title
      app_action_translations {
        language
        short_title
      }
      points
      user_has_paid_membership
      user_is_superfan
      has_multiplier_applied
      location_label

      user {
        ...UserAvatarAndName
      }
    }
  }
`);

const channelStore = useChannelStore();
const userStore = useUserStore();

const {
  data,
  executeQuery: refetch,
  fetching,
} = useQuery({
  query: ChannelRecentActivityDocument,
  variables: computed(() => ({
    channel_id: channelStore.channelSlug!,
    limit: 12,
    community_id: channelStore.selectedCommunity ?? null,
    __last_notification_id: lastNotificationId.value,
  })),
  pause: computed(() => !channelStore.channelSlug),
});

const loading = computed(() => fetching.value || !channelStore.channel);

watch(
  () => channelStore.lastLeaderboardUpdate,
  (newDate, oldDate) => {
    if ((newDate && !oldDate) || (newDate && oldDate && newDate > oldDate)) {
      refetch();
    }
  },
);

// ---- Date formatting -----------------------------------------------------

const timeFormatter = computed(
  () =>
    new Intl.RelativeTimeFormat(userStore.userLocale, { numeric: "always" }),
);

const unitsInSecondsMap = {
  second: 60,
  minute: 3600,
  hour: 86400,
  day: 86400 * 7,
  week: 86400 * 30,
  month: 86400 * 365,
  year: Infinity,
};

function formatRelativeDate(date: Date) {
  // Calculate the difference in seconds between the given date and the current date
  const secondsDiff = Math.round((date.getTime() - Date.now()) / 1000);

  let unit: keyof typeof unitsInSecondsMap = "second";
  let divisor = 1;

  for (const [key, value] of Object.entries(unitsInSecondsMap)) {
    unit = key as keyof typeof unitsInSecondsMap;

    if (value > Math.abs(secondsDiff)) {
      break;
    } else {
      divisor = value;
    }
  }

  // Format the relative time based on the calculated unit
  return timeFormatter.value.format(Math.floor(secondsDiff / divisor), unit);
}

// ---- Activities list (sorted, translated) --------------------------------

const { translateObject } = useTranslateObjectSimple();
const recentActivities = computed(() => {
  const list = (data.value?.recentUserChannelActivities ?? [])
    .filter((activity) => activity.points && activity.points > 0)
    .map((activity) => {
      const date = new Date(activity.date);
      const formattedDate = formatRelativeDate(date);
      const activityTranslations = translateObject<{ short_title: string }>({
        object: activity,
        objectTranslations: activity.app_action_translations,
        userLanguage: userStore.userLanguage,
      });

      return {
        ...activity,
        formattedDate,
        short_title: activityTranslations.short_title,
      };
    });

  list.sort((a, b) => {
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });

  return list;
});

/**
 * Sliced list used by the viewport-fit (new layout) path. The legacy path
 * (viewportFit=false) continues to iterate over the full `recentActivities`
 * list unchanged.
 *
 * In the viewport-fit path we must distinguish "not yet measured" from
 * "measured, zero fit": before the first ResizeObserver measurement the
 * container width is 0, and returning the full list then causes a flash of every
 * item on first paint. So we render NOTHING until `isMeasured`, then the
 * measured count.
 */
const visibleActivities = computed(() => {
  // Legacy path: full list, behaviour unchanged.
  if (!props.viewportFit) {
    return recentActivities.value;
  }
  // Viewport-fit path: hold back until measured to avoid the all-items flash.
  if (!isMeasured.value) {
    return [];
  }
  return recentActivities.value.slice(0, visibleCount.value);
});

type ActivityItem = (typeof recentActivities.value)[number];

/**
 * The human action label for the "Earn points for {action}" caption (new layout
 * only). Prefers the location label, then the (translated) action short title.
 * Returns "" when neither is present so the caption is suppressed entirely —
 * never rendering a dangling "Earn points for " with no action (some seed /
 * legacy actions carry an empty short_title and a null location_label).
 */
function activityActionLabel(activity: ActivityItem): string {
  return (activity.location_label ?? activity.short_title ?? "").trim();
}
</script>
